import type { TextNode } from '@repo/api-contracts';
import type { RenderMaterials } from '@repo/design-renderer';
import type { CSSProperties } from 'react';
import { FALLBACK_FONT_FAMILY } from './document-ops';
import type { FlatView } from './flat-mode-mapping';

/** 지금 바로 고치는 텍스트와 이번 고치기의 이름. */
export interface InlineEditing {
  readonly nodeId: string;
  readonly session: string;
}

let lastSession = 0;

/**
 * 글자 바로 고치기 한 번의 이름 — 한 번 여는 동안의 입력은 이 이름으로 묶어 undo 한 번으로 되돌린다.
 * 캔버스가 바뀌어도(평면↔곡면) 겹치지 않게 모듈에서 순번을 올린다.
 */
export const nextInlineTextSession = (nodeId: string): string => {
  lastSession += 1;
  return `inline-text:${nodeId}:${lastSession}`;
};

/**
 * 글자 바로 고치기 입력칸의 화면 모양 — 노드와 같은 서체·크기·색·정렬·자간·행간·줄 폭으로 노드 자리에 겹친다.
 * 입력칸은 가로 축척(pxPerMmX)으로 펴서 놓고, 노드 가운데 기준 CSS 변환으로 렌더와 같은 순서를 따른다 —
 * 화면 = 보기 축척(축마다 다름)·회전·세로 늘림. 그래서 바깥에 scale(1, pxY/pxX), 그 안에 rotate, 맨 안에 scaleY(scaleY) 를 둔다.
 */
export const inlineTextStyleOf = (
  node: TextNode,
  view: FlatView,
  materials: Pick<RenderMaterials, 'fonts' | 'inks'>,
): CSSProperties => {
  const pxX = view.pxPerMmX;
  const scaleY = node.scaleY ?? 1;
  const width = node.width * pxX;
  // 세로 늘림 전 레이아웃 높이 — 최소 한 줄은 둔다(빈 글자일 때도 캐럿이 보인다).
  const height = Math.max(node.height / scaleY, node.fontSizeMm * node.lineHeight) * pxX;
  const centerX = view.offsetX + (node.x + node.width / 2) * pxX;
  const centerY = view.offsetY + (node.y + node.height / 2) * view.pxPerMmY;
  const viewRatio = view.pxPerMmY / pxX;
  const family = materials.fonts.get(node.fontId)?.familyName ?? FALLBACK_FONT_FAMILY;
  const color = materials.inks.get(node.inkColorId) ?? '#000000';
  const decorations = [
    node.underline ? 'underline' : null,
    node.strikethrough ? 'line-through' : null,
  ].filter((value) => value !== null);
  return {
    left: centerX - width / 2,
    top: centerY - height / 2,
    width,
    height,
    transform: `scale(1, ${viewRatio}) rotate(${node.rotationDeg}deg) scaleY(${scaleY})`,
    transformOrigin: 'center',
    fontFamily: `"${family}"`,
    fontSize: node.fontSizeMm * pxX,
    lineHeight: node.lineHeight,
    letterSpacing: `${node.letterSpacingEm}em`,
    textAlign: node.align,
    fontWeight: node.bold ? 700 : 400,
    fontStyle: node.italic ? 'italic' : 'normal',
    textDecorationLine: decorations.length === 0 ? 'none' : decorations.join(' '),
    color,
    caretColor: color,
  };
};
